import React, {useEffect, useRef, useState} from 'react';
import {SearchOutlined} from '@ant-design/icons';
import {Link, useLocation} from 'react-router-dom';
import {AutoComplete, Empty, Input, Spin} from 'antd';
import {connect} from 'react-redux';
import {updateSearchBar} from '../../../redux/actions/Search';
import {getCustomers} from '../../../redux/actions/Customers';
import {getCars} from '../../../redux/actions/Cars';
import {getParts, getServices, getVendors} from '../../../redux/actions';
import useDebounce from 'hooks/useDebounce';
import AlgoliaService from 'services/AlgoliaService';
import Flex from 'components/shared-components/Flex';

const HighlightSpan = (props) => (
	<span dangerouslySetInnerHTML={{ __html: props.value }}></span>
);

const searchResult = (results) =>
	results.map((item) => {
		const highlights = item._highlightResult;
		const car =
			highlights.cars && highlights.cars.length > 0 ? highlights.cars[0] : null;
		return {
			key: item.id,
			value: item.id,
			label: (
				<Link to={`/app/customers/${item.id}`}>
					<div className="search-list-item">
						<p className="mb-0">
							<HighlightSpan value={highlights.name.value} /> -{' '}
							<HighlightSpan value={highlights.address_line_1.value} />,{' '}
							<HighlightSpan value={highlights.city.value} />{' '}
							<HighlightSpan value={highlights.state.value} />
							{car ? (
								<>
									<br />
									{car.year ? (
										<HighlightSpan value={car.year.value} />
									) : null}{' '}
									{car.make ? <HighlightSpan value={car.make.value} /> : null}{' '}
									{car.model ? <HighlightSpan value={car.model.value} /> : null}{' '}
									- {car.tag ? <HighlightSpan value={car.tag.value} /> : null} -
									VIN {car.vin ? <HighlightSpan value={car.vin.value} /> : null}
								</>
							) : null}
						</p>
					</div>
				</Link>
			),
		};
	});

const SearchInput = (props) => {
	const {
		active,
		close,
		isMobile,
		mode,
		placeholder,
		showSearch,
		searchFunction,
		updateSearchBar,
		getCustomers,
		customerPerPage,
		getCars,
		getServices,
		carsPage,
		customersPage,
		partsPerPage,
		partsPage,
		getParts,
		carsPerPage,
		servicesPage,
		servicesPerPage,
		getVendors,
		vendorsPage,
		vendorsPerPage,
	} = props;
	const [value, setValue] = useState('');
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const inputRef = useRef(null);
	const globalInputRef = useRef(null);
	const [showGlobalSearch, setShowGlobalSearch] = useState(false);
	const debouncedSearch = useDebounce(value, 2000);

	const onSelect = () => {
		setValue('');
		setOptions([]);
		if (close) {
			close();
		}
	};

	const onSearch = (searchText) => {
		setValue(searchText);
		switch (searchFunction) {
			case 'customers':
				getCustomers(customersPage, customerPerPage, searchText);
				break;
			case 'cars':
				getCars(carsPage, carsPerPage, searchText);
				break;
			case 'parts':
				getParts(partsPage, partsPerPage, searchText);
				break;
			case 'services':
				getServices(servicesPage, servicesPerPage, searchText);
				break;
			case 'vendors':
				getVendors(vendorsPage, vendorsPerPage, searchText);
				break;
			default:
				break;
		}
	};

	const autofocus = () => {
		inputRef.current.focus();
	};

	if (active) {
		autofocus();
	}

	let { pathname } = useLocation();

	// Called when a search has been triggered debounced by 1s.
	useEffect(() => {
		const perform = async () => {
			if (debouncedSearch.length > 0) {
				const res = await AlgoliaService.getCustomers({
					query: debouncedSearch,
					hitsPerPage: 10,
					page: 0,
				});
				setOptions(searchResult(res.data.hits));
			}
			setLoading(false);
		};

		if (showGlobalSearch) {
			perform();
		}
	}, [showGlobalSearch, debouncedSearch]);

	useEffect(() => {
		switch (pathname) {
			// case '/app/customers':
			// 	updateSearchBar('Filter Customers...', true, 'customers');
			// 	onSearch('');
			// 	setShowGlobalSearch(false);
			// 	break;
			// case '/app/cars':
			// 	updateSearchBar('Filter Cars...', true, 'cars');
			// 	onSearch('');
			// 	setShowGlobalSearch(false);
			// 	break;
			case '/app/inventory':
				updateSearchBar('Filter Parts...', true, 'parts');
				onSearch('');
				setShowGlobalSearch(false);
				break;
			case '/app/services':
				updateSearchBar('Filter Services...', true, 'services');
				onSearch('');
				setShowGlobalSearch(false);
				break;
			case '/app/vendors':
				updateSearchBar('Filter Vendors...', true, 'vendors');
				onSearch('');
				setShowGlobalSearch(false);
				break;
			default:
				updateSearchBar('Search', false, 'all');
				onSearch('');
				setShowGlobalSearch(true);
				break;
		}
		// eslint-disable-next-line
	}, [pathname, updateSearchBar]);

	return (
		<div className="position-relative">
			{showSearch && !showGlobalSearch ? (
				<AutoComplete
					ref={inputRef}
					className={`nav-search-input ${isMobile ? 'is-mobile' : ''} ${
						mode === 'light' ? 'light' : ''
					}`}
					dropdownClassName="nav-search-dropdown"
					options={options}
					onSelect={onSelect}
					onSearch={onSearch}
					value={value}
					filterOption={(inputValue, option) =>
						option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
					}
				>
					<Input
						placeholder={placeholder}
						prefix={<SearchOutlined className="mr-0" />}
					/>
				</AutoComplete>
			) : null}

			{!showSearch && showGlobalSearch ? (
				<AutoComplete
					ref={globalInputRef}
					className={`nav-search-input ${isMobile ? 'is-mobile' : ''} ${
						mode === 'light' ? 'light' : ''
					}`}
					dropdownClassName="nav-search-dropdown"
					options={loading ? [] : options}
					onSelect={onSelect}
					onSearch={onSearch}
					value={value}
					dropdownMatchSelectWidth={false}
					notFoundContent={
						loading ? (
							<Flex
								className="w-100"
								alignItems="center"
								justifyContent="center"
							>
								<Spin />
							</Flex>
						) : value.length === 0 ? (
							'Start typing to find customers...'
						) : (
							<Empty description="No results" />
						)
					}
				>
					<Input
						placeholder="Search"
						prefix={<SearchOutlined className="mr-0" />}
						onInput={(e) => setLoading(true)}
					/>
				</AutoComplete>
			) : null}
		</div>
	);
};

const mapStateToProps = ({search, customers, cars, parts, services, vendors}) => {
  const {showSearch, placeholder, searchFunction} = search
  const customerPerPage = customers.perPage
  const customersPage = customers.page
  const carsPerPage = cars.perPage
  const carsPage = cars.page
  const partsPage = parts.page
  const partsPerPage = parts.perPage
  const servicesPage = services.page
  const servicesPerPage = services.perPage
  const vendorsPage = vendors.page;
  const vendorsPerPage = vendors.perPage;

  return {
    showSearch,
    placeholder,
    searchFunction,
    customerPerPage,
    carsPerPage,
    customersPage,
    partsPage,
    carsPage,
    partsPerPage,
    servicesPage,
    servicesPerPage,
    vendorsPage,
    vendorsPerPage,
  };
}

const mapDispatchToProps = {
  updateSearchBar,
  getCustomers,
  getParts,
  getCars,
  getServices,
  getVendors,
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchInput)
